import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { setupNavigation } from "../../redux/actions/navItemsActions";
import { loadUserProfile } from "../../redux/actions/userActions";
import { updateGeneralSettings, updateSiteSettings, uploadAvatar } from "../../redux/actions/settingsActions";
import SettingsGeneralForm from "./SettingsGeneralForm";
import SettingsSiteForm from "./SettingsSiteForm";
import SettingsAvatarForm from "./SettingsAvatarForm";
import SettingsPasswordForm from "./SettingsPasswordForm";
import { changePassword } from "../../redux/actions/authActions";
import Spinner from "../common/Spinner";
import { toast } from "react-toastify";

function SettingsPage({
    auth,
    setupNavigation,
    loadUserProfile,
    updateGeneralSettings, 
    updateSiteSettings,
    uploadAvatar,
    settings,
    changePassword,
    history,
    ...props
}) {

    const [activeForm, setActiveForm] = useState({ form: "general" });
    const [errors, setErrors] = useState({});
    const [processing, setProcessing] = useState(true);
    const [passwordDetails, setPasswordDetails] = useState({ ...props.passwordDetails });
    const [settingDetails, setSettingDetails] = useState({
        firstName: "",
        surname: "",
        email: "",
        location: "",
        preferredMeasurementUnits: "0",
        subscribedMarketingNotification: false,
        subscribedRideBlogsNotification: false,
        approveFollowers: false,
        avatar: { src: "", preview: "" }
    });

    const navigationItems =
    {
        sections: [{
            name: 'Settings',
            items: [
                {
                    path: '/user/settings',
                    text: 'Settings',
                    icon: 'fa-cog'
                }
            ]
        },
        {
            name: 'Settings',
            items: [
                {
                    path: 'user/settings/general', 
                    text: 'General Settings',
                    icon: 'fa-tasks'
                },
                {
                    path: 'user/settings/site', 
                    text: 'Site Settings',
                    icon: 'fa-cogs'
                },
                {
                    path: 'user/settings/avatar', 
                    text: 'Avatar',
                    icon: 'fa-user'
                },
                {
                    path: 'user/settings/security', 
                    text: 'Security',
                    icon: 'fa-key'
                }
            ]
        }
        ]
    };

    useEffect(() => {
        document.title = "User Settings | JustGoRide";
        setupNavigation(navigationItems);
        if (eval(!auth.isAuthenticated)) {
            history.push("/login");
        }
        if (!settings.email) {
            setProcessing(true);
            loadUserProfile(auth.userId, auth.accessToken)
                .then((s) => {
                    s.avatarDetails = { src: "", preview: s.avatar }
                    setSettingDetails(s);
                    setProcessing(false);
                })
        }
        else {
            settings.avatarDetails = { src: "", preview: settings.avatar }
            setSettingDetails(settings); 
            setProcessing(false);
        }
    }, []);

    function setForm(e) {
        var form = e.target.name;
        setActiveForm(setActiveForm => ({
            ...setActiveForm,
            form: form
        }));
    }

    function handleChange(event) {
        var { name, value } = event.target;
        if (name === "subscribedMarketingNotification") {
            value = event.target.checked; 
        }
        if (name === "subscribedRideBlogsNotification") {
            value = event.target.checked; 
        }
        if (name === "approveFollowers") {
            value = event.target.checked;
        }

        setSettingDetails(prevSettingDetails => ({
            ...prevSettingDetails,
            [name]: value
        }));
        setErrors(prevErrors => ({
            ...prevErrors,
            [name]: undefined
        }));
    }

    function handlePasswordChange(event) {
        const { name, value } = event.target;
        setPasswordDetails(prevPasswordDetails => ({
            ...prevPasswordDetails,
            [name]: value
        }));
        setErrors(prevErrors => ({
            ...prevErrors,
            [name]: undefined
        }));
    }

    function generalFormIsValid() {
        const errors = {};
        var isValid = true;

        if (!settingDetails.firstName) {
            errors.firstName = "First Name is required.";
            setErrors(errors);
            isValid = false;
        }

        if (!settingDetails.surname) {
            errors.surname = "Surname is required.";
            setErrors(errors);
            isValid = false;
        }

        if (!settingDetails.email) {
            errors.email = "Email address is required.";
            setErrors(errors);
            isValid = false;
        } else if (!/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(settingDetails.email)) {
            errors.email = "Email address format invalid.";
            setErrors(errors);
            isValid = false;
        }

        return isValid;
    }
    
    function handleGeneralSave(event) {
        event.preventDefault();

        if (!generalFormIsValid()) return; 
        setProcessing(true);
        updateGeneralSettings(settingDetails).then(() => {
            toast.success("General Settings Saved");
            setErrors({}); 
            setProcessing(false);
        })
        .catch(error => {
            setProcessing(false);
            setErrors({ onGeneralSave: error.message });
        });
    }

     function siteFormIsValid() {
        const errors = {};
        var isValid = true;

        if (settingDetails.preferredMeasurementUnits != "0" && settingDetails.preferredMeasurementUnits != "1") {
            errors.preferredMeasurementUnits = "Please select a measurement unit";
            setErrors(errors);
            isValid = false;
        }

        return isValid;
    }

    function passwordFormIsValid() {
        const errors = {};
        var isValid = true;

        if (!passwordDetails.currentPassword) {
            errors.currentPassword = "You provide your current password.";
            setErrors(errors);
            isValid = false;
        }

        if (!passwordDetails.newPassword) {
            errors.newPassword = "You must specify a new password.";
            setErrors(errors);
            isValid = false;
        }

        if (!passwordDetails.newPasswordConfirmation) {
            errors.newPasswordConfirmation = "You must confirm the password.";
            setErrors(errors);
            isValid = false;
        }

        if (passwordDetails.newPassword !== passwordDetails.newPasswordConfirmation) {
            errors.newPasswordConfirmation = "The passwords must match.";
            setErrors(errors);
            isValid = false;
        }

        var passwordStrength = new RegExp("^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.{8,})");
        if (!passwordStrength.test(passwordDetails.newPassword)) {
            errors.newPassword = "Password must be at least 8 charactors in length, contain upper and lowecase letters and at least one number.";
            setErrors(errors);
            isValid = false;
        }
        return isValid;
    }
    
    function handleSiteSave(event) {
        event.preventDefault();

        if (!siteFormIsValid()) return;
        setProcessing(true);
        updateSiteSettings(settingDetails).then(() => {
            toast.success("Site Settings Saved");
            setErrors({}); 
            setProcessing(false);
        })
        .catch(error => {
            setProcessing(false);
            setErrors({ onSiteSave: error.message });
        });

    }

    function handlePasswordSave(event) {
        event.preventDefault();

        if (!passwordFormIsValid()) return;
        setProcessing(true);
        changePassword(passwordDetails).then(() => {
            toast.success("Password changed");
            setErrors({}); 
            setProcessing(false);
        })
        .catch(error => {
            setProcessing(false);
            setErrors({ onPasswordSave: error.message });
        });
    }

    //AVATAR
    function onAvatarClose() {
  
         var a = { src: settingDetails.avatar.src, preview: settingDetails.avatar };

        setSettingDetails(prevSettingDetails => ({
            ...prevSettingDetails,
            avatarDetails: a
        }));
    }


    function onAvatarCrop(avatarPreview) {
        //alert("avatar cropped");
        
        var a = { src: settingDetails.avatar.src, preview: avatarPreview };

        setSettingDetails(prevSettingDetails => ({
            ...prevSettingDetails,
            avatarDetails: a
        }));
    }


    function onAvatarBeforeFileLoad(elem) {
        //alert("avatar before file load");

        //if (elem.target.files[0].size > 71680) {
        //    alert("File is too big!");
        //    elem.target.value = "";
        //};

    }

    function onAvatarSave(event) {
         event.preventDefault();
        
        setProcessing(true);
        uploadAvatar(settingDetails).then(() => {
            toast.success("Avatar Saved");
            setErrors({}); 
            setProcessing(false);
        })
        .catch(error => {
            setProcessing(false);
            setErrors({ onAvatarSave: error.message });
        });
    }

    return (
        processing ?
        <Spinner /> :
        <div className="container">
            <div className="row justify-content-md-center mt-5" >
                <div className="btn-group btn-group-toggle" data-toggle="buttons">
                    <button name="general" className={ activeForm.form == "general" ? "btn btn-outline-primary active" : "btn btn-outline-secondary" } onClick={ setForm } >
                        General Settings
                    </button>
                    <button name="site" className={ activeForm.form == "site" ?  "btn btn-outline-primary active" : "btn btn-outline-secondary" } onClick={ setForm } >
                        Site Settings
                    </button>
                    <button name="avatar" className={ activeForm.form == "avatar" ? "btn btn-outline-primary active" : "btn btn-outline-secondary" } onClick={ setForm } >
                        Avatar
                    </button>
                    <button name="password" className={ activeForm.form == "password" ? "btn btn-outline-primary active" : "btn btn-outline-secondary" } onClick={ setForm }>
                        Password
                    </button>
                </div>
            </div>
            <div>
                    {
                        (() => {
                            switch (activeForm.form) {
                                case "general":
                                    return (
                                        <div className="row justify-content-md-center">
                                            <SettingsGeneralForm
                                                settings={settingDetails}
                                                errors={errors}
                                                onChange={handleChange}
                                                onSave={handleGeneralSave}
                                                processing={processing}
                                            />
                                        </div>
                                    )
                                case 'site':
                                    return (
                                        <div className="row justify-content-md-center">
                                            <SettingsSiteForm
                                                settings={settingDetails}
                                                errors={errors}
                                                onChange={handleChange}
                                                onSave={handleSiteSave}
                                                processing={processing}
                                            />
                                        </div>
                                    )
                                case 'avatar':
                                    return (
                                        <div className="row justify-content-md-center">
                                            <SettingsAvatarForm
                                                avatar={settingDetails.avatarDetails}
                                                title="Your Avatar"
                                                errors={errors}
                                                onCrop={onAvatarCrop}
                                                onClose={onAvatarClose}
                                                onBeforeFileLoad={onAvatarBeforeFileLoad}
                                                onSave={onAvatarSave}
                                                processing={processing}
                                            />
                                        </div>
                                    )
                                case 'password':
                                    return (
                                        <div className="row justify-content-md-center">
                                            <SettingsPasswordForm
                                                passwordDetails={passwordDetails}
                                                errors={errors}
                                                onChange={handlePasswordChange}
                                                onChangePassword={handlePasswordSave}
                                                processing={processing}
                                            />
                                        </div>
                                    )
                                default:
                                    return null;
                            }
                        })()
                    }
            </div>
        </div> 
    );

}



SettingsPage.propTypes =
    {
        setupNavigation: PropTypes.func.isRequired, 
        loadUserProfile: PropTypes.func.isRequired,
        updateGeneralSettings: PropTypes.func.isRequired, 
        updateSiteSettings: PropTypes.func.isRequired, 
        uploadAvatar: PropTypes.func.isRequired, 
        settings: PropTypes.object.isRequired,
        passwordDetails: PropTypes.object.isRequired,
        changePassword: PropTypes.func.isRequired,
        auth: PropTypes.object.isRequired,
        errorMessage: PropTypes.string
    }


function mapStateToProps(state) {
    return {
        settings: state.settings,
        passwordDetails: { currentPassword: "", newPassword: "", newPasswordConfirmation: "" },
        auth: state.auth
    };
}

const mapDispatchToProps = {
    setupNavigation,
    loadUserProfile,
    updateGeneralSettings, 
    updateSiteSettings,
    uploadAvatar,
    changePassword 
}

export default connect(mapStateToProps, mapDispatchToProps)(SettingsPage); 

/*
 *
onCrop={handleChange}
onClose={handleChange}
onBeforeFileLoad={handleChange}
*/





// WEBPACK FOOTER //
// ./src/components/user/SettingsPage.js